<div
  class="rich-text-editor rounded-xl border bg-white transition"
  [class.border-indigo-500]="isFocused"
  [class.ring-2]="isFocused"
  [class.ring-indigo-100]="isFocused"
  [class.border-gray-300]="!isFocused"
>
  @if (canEdit) {
    <div
      class="flex flex-wrap items-center gap-1 border-b border-gray-200 bg-gray-50/80 px-2 py-1.5 rounded-t-xl"
      role="toolbar"
      [attr.aria-label]="'RICH_TEXT.TOOLBAR' | translate"
    >
      <!-- Bold / Italic / Underline -->
      <button
        type="button"
        class="rich-text-tool"
        [class.is-active]="isActive('bold')"
        [attr.aria-pressed]="isActive('bold')"
        [title]="'RICH_TEXT.BOLD' | translate"
        [attr.aria-label]="'RICH_TEXT.BOLD' | translate"
        (mousedown)="$event.preventDefault()"
        (click)="toggleBold()"
      >
        <fa-icon [icon]="icons.bold"></fa-icon>
      </button>

      <button
        type="button"
        class="rich-text-tool"
        [class.is-active]="isActive('italic')"
        [attr.aria-pressed]="isActive('italic')"
        [title]="'RICH_TEXT.ITALIC' | translate"
        [attr.aria-label]="'RICH_TEXT.ITALIC' | translate"
        (mousedown)="$event.preventDefault()"
        (click)="toggleItalic()"
      >
        <fa-icon [icon]="icons.italic"></fa-icon>
      </button>

      <button
        type="button"
        class="rich-text-tool"
        [class.is-active]="isActive('underline')"
        [attr.aria-pressed]="isActive('underline')"
        [title]="'RICH_TEXT.UNDERLINE' | translate"
        [attr.aria-label]="'RICH_TEXT.UNDERLINE' | translate"
        (mousedown)="$event.preventDefault()"
        (click)="toggleUnderline()"
      >
        <fa-icon [icon]="icons.underline"></fa-icon>
      </button>

      <span class="mx-1 h-5 w-px bg-gray-300" aria-hidden="true"></span>

      <!-- Font family / size -->
      <select
        class="rich-text-select w-36"
        [value]="activeFontFamily"
        [title]="'RICH_TEXT.FONT' | translate"
        [attr.aria-label]="'RICH_TEXT.FONT' | translate"
        (change)="setFontFamily($any($event.target).value)"
      >
        <option value="">{{ 'RICH_TEXT.DEFAULT_FONT' | translate }}</option>
        @for (font of fontFamilies; track font) {
          <option [value]="font" [style.font-family]="font">{{ font }}</option>
        }
      </select>

      <select
        class="rich-text-select w-24"
        [value]="activeFontSize"
        [title]="'RICH_TEXT.SIZE' | translate"
        [attr.aria-label]="'RICH_TEXT.SIZE' | translate"
        (change)="setFontSize($any($event.target).value)"
      >
        <option value="">{{ 'RICH_TEXT.DEFAULT_SIZE' | translate }}</option>
        @for (size of fontSizes; track size) {
          <option [value]="size">{{ size }}</option>
        }
      </select>

      <span class="mx-1 h-5 w-px bg-gray-300" aria-hidden="true"></span>

      <!-- Text colour -->
      <div class="relative">
        <button
          type="button"
          class="rich-text-tool"
          [class.is-active]="openPalette === 'color'"
          [attr.aria-expanded]="openPalette === 'color'"
          [title]="'RICH_TEXT.TEXT_COLOR' | translate"
          [attr.aria-label]="'RICH_TEXT.TEXT_COLOR' | translate"
          (mousedown)="$event.preventDefault()"
          (click)="togglePalette('color')"
        >
          <fa-icon [icon]="icons.color"></fa-icon>
        </button>

        @if (openPalette === 'color') {
          <div class="rich-text-palette">
            @for (color of colors; track color) {
              <button
                type="button"
                class="rich-text-swatch"
                [style.background-color]="color"
                [title]="color"
                [attr.aria-label]="color"
                (mousedown)="$event.preventDefault()"
                (click)="setColor(color)"
              ></button>
            }
            <button
              type="button"
              class="rich-text-palette-reset"
              (mousedown)="$event.preventDefault()"
              (click)="setColor(null)"
            >
              {{ 'RICH_TEXT.RESET' | translate }}
            </button>
          </div>
        }
      </div>

      <!-- Highlight -->
      <div class="relative">
        <button
          type="button"
          class="rich-text-tool"
          [class.is-active]="openPalette === 'highlight' || isActive('highlight')"
          [attr.aria-expanded]="openPalette === 'highlight'"
          [title]="'RICH_TEXT.HIGHLIGHT' | translate"
          [attr.aria-label]="'RICH_TEXT.HIGHLIGHT' | translate"
          (mousedown)="$event.preventDefault()"
          (click)="togglePalette('highlight')"
        >
          <fa-icon [icon]="icons.highlight"></fa-icon>
        </button>

        @if (openPalette === 'highlight') {
          <div class="rich-text-palette">
            @for (color of highlights; track color) {
              <button
                type="button"
                class="rich-text-swatch"
                [style.background-color]="color"
                [title]="color"
                [attr.aria-label]="color"
                (mousedown)="$event.preventDefault()"
                (click)="setHighlight(color)"
              ></button>
            }
            <button
              type="button"
              class="rich-text-palette-reset"
              (mousedown)="$event.preventDefault()"
              (click)="setHighlight(null)"
            >
              {{ 'RICH_TEXT.RESET' | translate }}
            </button>
          </div>
        }
      </div>

      <span class="mx-1 h-5 w-px bg-gray-300" aria-hidden="true"></span>

      <!-- Lists -->
      <button
        type="button"
        class="rich-text-tool"
        [class.is-active]="isActive('bulletList')"
        [attr.aria-pressed]="isActive('bulletList')"
        [title]="'RICH_TEXT.BULLET_LIST' | translate"
        [attr.aria-label]="'RICH_TEXT.BULLET_LIST' | translate"
        (mousedown)="$event.preventDefault()"
        (click)="toggleBulletList()"
      >
        <fa-icon [icon]="icons.bulletList"></fa-icon>
      </button>

      <button
        type="button"
        class="rich-text-tool"
        [class.is-active]="isActive('orderedList')"
        [attr.aria-pressed]="isActive('orderedList')"
        [title]="'RICH_TEXT.NUMBERED_LIST' | translate"
        [attr.aria-label]="'RICH_TEXT.NUMBERED_LIST' | translate"
        (mousedown)="$event.preventDefault()"
        (click)="toggleOrderedList()"
      >
        <fa-icon [icon]="icons.orderedList"></fa-icon>
      </button>

      <span class="mx-1 h-5 w-px bg-gray-300" aria-hidden="true"></span>

      <button
        type="button"
        class="rich-text-tool"
        [title]="'RICH_TEXT.CLEAR_FORMATTING' | translate"
        [attr.aria-label]="'RICH_TEXT.CLEAR_FORMATTING' | translate"
        (mousedown)="$event.preventDefault()"
        (click)="clearFormatting()"
      >
        <fa-icon [icon]="icons.clear"></fa-icon>
      </button>

      <!-- Table: shown while the cursor is in a table; acts on the cursor's row / column -->
      @if (isInTable) {
        <span class="mx-1 h-5 w-px bg-gray-300" aria-hidden="true"></span>

        <div class="rich-text-table-actions" role="group" [attr.aria-label]="'RICH_TEXT.TABLE' | translate">
          <span class="rich-text-table-actions__label">{{ 'RICH_TEXT.TABLE' | translate }}</span>

          @for (part of tableParts; track part.key) {
            <button
              type="button"
              class="rich-text-table-action"
              [class.rich-text-table-action--table]="part.key === 'table'"
              [title]="part.label | translate"
              (mousedown)="$event.preventDefault()"
              (mouseenter)="showDeletePreview(part.key)"
              (mouseleave)="hideDeletePreview()"
              (focus)="showDeletePreview(part.key)"
              (blur)="hideDeletePreview()"
              (click)="deleteTablePart(part.key)"
            >
              <fa-icon [icon]="icons.delete"></fa-icon>
              <span>{{ part.label | translate }}</span>
            </button>
          }
        </div>
      }
    </div>
  }

  <div
    #editorArea
    class="relative cursor-text px-4 py-3"
    (click)="focusEditor()"
  >
    @if (isEmpty && placeholder && canEdit) {
      <span class="pointer-events-none absolute start-4 top-3 text-gray-400" aria-hidden="true">
        {{ placeholder }}
      </span>
    }

    <!-- What the hovered table action will delete -->
    @if (deletePreview) {
      <div
        class="rich-text-delete-preview"
        [style.top.px]="deletePreview.top"
        [style.left.px]="deletePreview.left"
        [style.width.px]="deletePreview.width"
        [style.height.px]="deletePreview.height"
        aria-hidden="true"
      ></div>
    }


    <div
      #editorHost
      [style.min-height]="minHeight"
      [attr.aria-placeholder]="placeholder || null"
    ></div>
  </div>
</div>
